'use client';
import { PremiumBackground } from '../effects/PremiumBackground';

import React from 'react';
import { motion } from 'framer-motion';
import { 
ArrowDown, 
  Coffee, 
  Lock, 
  TrendingUp, 
  BarChart3, 
  Compass, 
  Users, 
  ShieldAlert, 
  KeyRound, 
  Boxes, 
  Scale, 
  Rocket, 
  Zap, 
  CheckCircle2 
} from 'lucide-react';

// Drop-in Link component for Vite React SPA
const Link = ({
  href,
  children,
  className,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) => (
  <a href={href} className={className} {...props}>
    {children}
  </a>
);

export interface ClinicOSProps {
  id?: string;
}

export function ClinicOS({ id = 'clinicos' }: ClinicOSProps = {}) {
  // Микроразметка Schema.org для нейросетей (SearchGPT, Perplexity, Яндекс Нейро, Google AI)
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "S&P Clinic OS™ (Управленческий Паспорт Медицинского Актива)",
    "brand": {
      "@type": "Organization",
      "name": "Structure & Partners"
    },
    "description": "Операционная система управления частной медицинской клиникой, ликвидирующая зависимость бизнеса от основателя (Key-Man Risk). Двухуровневый контур контроля: Контур 'На столе' (Daily P&L, МИС, загрузка кресел, KPI врачей) и Контур 'В сейфе' (50+ СОПов, 98-ФЗ Коммерческая тайна, ПП РФ № 2129 и № 736), а также инвестиционный рычаг роста мощности до 7.5x EBITDA.",
    "category": "Healthcare Management System / M&A Asset Passport",
    "hasPart": [
      {
        "@type": "HowToSection",
        "name": "Контур 'На столе' (Операционный пульт управления)",
        "itemListElement": [
          "Daily P&L и казначейский календарь связки МИС и 1С",
          "Светофор коммерции: CAC, конверсия звонков и загрузка кресел",
          "Трекер эффективности врачей: выработка по часам и Retention"
        ]
      },
      {
        "@type": "HowToSection",
        "name": "Контур 'В сейфе' (Регламенты и регуляторный щит)",
        "itemListElement": [
          "Кассовая дисциплина Анти-вор и сдельный расчет ФОТ",
          "Режим Коммерческой тайны (98-ФЗ) и опционы хирургов",
          "Паспорта медтехники по ПП РФ № 2129 и списание со счета 10",
          "Лицензионный чемоданчик СЭС, Росздравнадзора и ПП РФ № 736"
        ]
      }
    ]
  };

  return (
    <section 
      id={id} 
      className="relative w-full py-24 md:py-32 bg-[#FAF8F5] text-[#141416] border-t border-[#EAE5DE] overflow-hidden"
      aria-label="Операционная система клиники: S&P Clinic OS"
    >
      {/* ТАКТИЛЬНЫЙ МАКРО-РЕЛЬЕФ */}
      <PremiumBackground variant={5} />

      
      

      <div id={`${id}-anchor`} className="absolute -top-16 left-0" />
      
      {/* Light subtle archival paper gradient */}
      <div className="absolute top-0 left-0 right-0 h-48 bg-gradient-to-b from-[#F2EDE4]/60 to-transparent pointer-events-none" />

      {/* Скрытая разметка JSON-LD для ИИ-поисковиков */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />

      <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 flex flex-col gap-16 md:gap-20">
        
        {/* =========================================================================
            1. НАДЗАГОЛОВОК, H2 И ПОДЗАГОЛОВОК-МАНИФЕСТ
            ========================================================================= */}
        <div className="flex flex-col gap-6 max-w-4xl">
          <div className="luxury-eyebrow text-[#8C6D37] flex items-center gap-2.5 font-bold tracking-[0.2em] uppercase">
            <span className="w-1.5 h-1.5 rounded-none bg-[#8C6D37]" />
            ЭТАЖ 9 // УПРАВЛЕНЧЕСКИЙ ПАСПОРТ АКТИВА // S&P CLINIC OS™
          </div>

          <h2 className="text-3xl sm:text-5xl lg:text-[54px] font-serif font-semibold tracking-tight leading-[1.1] text-[#141416]">
            S&P Clinic OS™: Архитектура автономии <br />
            <span className="text-[#64748B] italic font-serif">и ликвидация зависимости от основателя.</span>
          </h2>

          <p className="text-[16px] sm:text-[17px] text-[#334155] leading-[1.65] font-sans font-normal max-w-3xl border-l-[4px] border-[#8C6D37] pl-6 mt-4">
            Инвесторы платят максимальную цену не за прошлые подвиги владельца, а за систему, 
            которая генерирует подтвержденную прибыль без его личного участия. <br />
            <strong>S&P Clinic OS™</strong> — это операционная система клиники, превращающая разрозненные 
            процессы в прозрачный бизнес инвестиционного класса из двух уровней контроля.
          </p>
        </div>

        {/* =========================================================================
            2. ДВУХУРОВНЕВАЯ АРХИТЕКТУРА КОНТРОЛЯ (СПЛИТ-БЛОК: НА СТОЛЕ / В СЕЙФЕ)
            ========================================================================= */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
          
          {/* КОНТУР 1: «НА СТОЛЕ» (ПУЛЬТ УПРАВЛЕНИЯ) */}
          <div className="p-8 sm:p-10 bg-white border border-[#EAE5DE] shadow-xs rounded-[2px] flex flex-col justify-between gap-8 group hover:border-[#CBD5E1] transition-colors">
            <div className="flex flex-col gap-8">
              
              {/* Заголовок контура */}
              <div className="flex items-center justify-between pb-5 border-b border-[#EAE5DE]">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-[#F7F4EE] border border-[#EAE5DE] rounded-[2px] group-hover:border-[#CBD5E1] transition-colors">
                    <Coffee size={18} className="text-[#8C6D37]" />
                  </div>
                  <div>
                    <span className="luxury-eyebrow text-[#8C6D37] font-bold block mb-1 uppercase tracking-wider">
                      КОНТУР 1
                    </span>
                    <h3 className="text-2xl font-serif font-semibold text-[#141416]">
                      «НА СТОЛЕ» (Операционный пульт)
                    </h3>
                  </div>
                </div>
              </div>

              <p className="text-[13px] text-[#64748B] font-mono leading-relaxed font-semibold">
                То, на что инвестор, генеральный директор или собственник смотрит каждое утро за чашкой кофе:
              </p>

              {/* 3 блока пульта */}
              <div className="flex flex-col gap-6">
                
                {/* 1. Финансы */}
                <div className="flex flex-col gap-2 p-5 bg-[#FAF8F5] border border-[#EAE5DE] rounded-[2px]">
                  <div className="flex items-center gap-2">
                    <BarChart3 size={16} className="text-[#8C6D37]" />
                    <h4 className="text-[15px] font-sans font-bold text-[#141416]">
                      Финансы и казначейство (Daily P&L & Cash Flow):
                    </h4>
                  </div>
                  <p className="text-[13px] text-[#334155] leading-relaxed font-sans font-normal">
                    Сквозная связка медицинской программы (МИС) и 1С. План/факт выручки по начислению, 
                    оперативный контроль кассовых разрывов, расчет чистого операционного денежного потока.
                  </p>
                  <span className="font-mono text-[11px] text-[#8C6D37] font-bold pt-1">
                    Ответ на вопрос: «Сколько чистой прибыли клиника сгенерировала сегодня?»
                  </span>
                </div>

                {/* 2. Светофор */}
                <div className="flex flex-col gap-2 p-5 bg-[#FAF8F5] border border-[#EAE5DE] rounded-[2px]">
                  <div className="flex items-center gap-2">
                    <Compass size={16} className="text-[#8C6D37]" />
                    <h4 className="text-[15px] font-sans font-bold text-[#141416]">
                      Светофор коммерции и маркетинга:
                    </h4>
                  </div>
                  <p className="text-[13px] text-[#334155] leading-relaxed font-sans font-normal">
                    Автоматическая аналитика воронки. Стоимость привлечения пациента (CAC), процент 
                    загрузки каждого кресла по часам, конверсия администраторов из входящего звонка в дошедшего до приема пациента.
                  </p>
                  <span className="font-mono text-[11px] text-[#8C6D37] font-bold pt-1">
                    Ответ на вопрос: «Где мы теряем пациентов и деньги прямо сейчас?»
                  </span>
                </div>

                {/* 3. Трекер KPI */}
                <div className="flex flex-col gap-2 p-5 bg-[#FAF8F5] border border-[#EAE5DE] rounded-[2px]">
                  <div className="flex items-center gap-2">
                    <Users size={16} className="text-[#8C6D37]" />
                    <h4 className="text-[15px] font-sans font-bold text-[#141416]">
                      Командный трекер эффективности (KPI):
                    </h4>
                  </div>
                  <p className="text-[13px] text-[#334155] leading-relaxed font-sans font-normal">
                    Оцифровка медицинского штата. Выработка врачей по кресло-часам, соблюдение комплексных 
                    планов лечения, процент удержания и возвращаемости пациентов (Retention).
                  </p>
                  <span className="font-mono text-[11px] text-[#8C6D37] font-bold pt-1">
                    Ответ на вопрос: «Кто из докторов работает на результат, а кто сливает базу?»
                  </span>
                </div>

              </div>
            </div>

            <div className="p-4 bg-[#F7F4EE] border border-[#EAE5DE] rounded-[2px] font-mono text-[11px] text-[#141416] font-bold uppercase tracking-wider text-center shadow-xs">
              Результат: Инвестор видит операционку в реальном времени с планшета
            </div>
          </div>

          {/* КОНТУР 2: «В СЕЙФЕ» (ФУНДАМЕНТ БЕЗОПАСНОСТИ И СОПЫ) */}
          <div className="p-8 sm:p-10 bg-white border border-[#EAE5DE] shadow-xs rounded-[2px] flex flex-col justify-between gap-8 group hover:border-[#CBD5E1] transition-colors">
            <div className="flex flex-col gap-8">
              
              {/* Заголовок контура */}
              <div className="flex items-center justify-between pb-5 border-b border-[#EAE5DE]">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-[#F7F4EE] border border-[#EAE5DE] rounded-[2px] group-hover:border-[#CBD5E1] transition-colors">
                    <Lock size={18} className="text-[#64748B]" />
                  </div>
                  <div>
                    <span className="luxury-eyebrow text-[#64748B] font-bold block mb-1 uppercase tracking-wider">
                      КОНТУР 2
                    </span>
                    <h3 className="text-2xl font-serif font-semibold text-[#141416]">
                      «В СЕЙФЕ» (Фундамент безопасности)
                    </h3>
                  </div>
                </div>
              </div>

              <p className="text-[13px] text-[#64748B] font-mono leading-relaxed font-semibold">
                50+ проприетарных СОПов (стандартных операционных процедур), защищающих актив от проверок и саботажа:
              </p>

              {/* 4 блока сейфа */}
              <div className="flex flex-col gap-4">
                
                {/* 1. Деньги и касса */}
                <div className="flex flex-col gap-1.5 p-4 bg-[#FAF8F5] border border-[#EAE5DE] rounded-[2px]">
                  <div className="flex items-center gap-2">
                    <ShieldAlert size={15} className="text-[#8C6D37]" />
                    <h4 className="text-[14px] font-sans font-bold text-[#141416]">
                      Деньги и кассовый контроль (Система «Анти-вор»):
                    </h4>
                  </div>
                  <p className="text-[13px] text-[#334155] leading-relaxed font-sans font-normal">
                    Регламенты кассовой дисциплины, исключающие расчеты мимо кассы, платежный календарь, 
                    автоматический калькулятор сдельной оплаты труда без ручных манипуляций.
                  </p>
                </div>

                {/* 2. Кадровый замок */}
                <div className="flex flex-col gap-1.5 p-4 bg-[#FAF8F5] border border-[#EAE5DE] rounded-[2px]">
                  <div className="flex items-center gap-2">
                    <KeyRound size={15} className="text-[#8C6D37]" />
                    <h4 className="text-[14px] font-sans font-bold text-[#141416]">
                      Кадровый замок и защита базы (98-ФЗ):
                    </h4>
                  </div>
                  <p className="text-[13px] text-[#334155] leading-relaxed font-sans font-normal">
                    Внедрение юридического режима Коммерческой тайны, регламенты безопасного найма, 
                    договоры о полной материальной ответственности, программы опционов и удержания ведущих хирургов.
                  </p>
                </div>

                {/* 3. Оборудование */}
                <div className="flex flex-col gap-1.5 p-4 bg-[#FAF8F5] border border-[#EAE5DE] rounded-[2px]">
                  <div className="flex items-center gap-2">
                    <Boxes size={15} className="text-[#8C6D37]" />
                    <h4 className="text-[14px] font-sans font-bold text-[#141416]">
                      Оборудование и складской порядок:
                    </h4>
                  </div>
                  <p className="text-[13px] text-[#334155] leading-relaxed font-sans font-normal">
                    Паспорта и действующие Регистрационные Удостоверения Росздравнадзора на каждый аппарат, 
                    графики планового ТО по Постановлению № 2129, жесткие нормы списания медикаментов со счета 10.
                  </p>
                </div>

                {/* 4. Лицензионный щит */}
                <div className="flex flex-col gap-1.5 p-4 bg-[#FAF8F5] border border-[#EAE5DE] rounded-[2px]">
                  <div className="flex items-center gap-2">
                    <Scale size={15} className="text-[#8C6D37]" />
                    <h4 className="text-[14px] font-sans font-bold text-[#141416]">
                      Лицензионный и регуляторный щит:
                    </h4>
                  </div>
                  <p className="text-[13px] text-[#334155] leading-relaxed font-sans font-normal">
                    Папка «Тревожный чемоданчик» на случай внеплановых визитов надзорных органов 
                    (СЭС, Прокуратура, Росздравнадзор), журналы производственного контроля, шаблоны договоров по Постановлению № 736.
                  </p>
                </div>

              </div>
            </div>

            <div className="p-4 bg-[#F7F4EE] border border-[#EAE5DE] rounded-[2px] font-mono text-[11px] text-[#334155] font-bold uppercase tracking-wider text-center shadow-xs">
              Результат: Бронежилет против штрафов, внезапных проверок и воровства
            </div>
          </div>

        </div>

        {/* =========================================================================
            3. КОНТУР 3. «ИНВЕСТИЦИОННЫЙ РЫЧАГ» (ДОРОЖНАЯ КАРТА УДВОЕНИЯ КАПИТАЛИЗАЦИИ)
            ========================================================================= */}
        <div className="p-8 sm:p-10 border border-[#8C6D37]/50 bg-[#8C6D37]/10 flex flex-col gap-8 rounded-[2px] shadow-xs relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-[#8C6D37]" />
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-[#8C6D37]/20 pt-2">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-white border border-[#8C6D37]/30 rounded-[2px]">
                <Rocket size={20} className="text-[#8C6D37]" />
              </div>
              <div>
                <span className="luxury-eyebrow text-[#8C6D37] block mb-1 font-bold uppercase tracking-wider">
                  КОНТУР 3 // VALUE CREATION
                </span>
                <h3 className="text-2xl font-serif font-semibold text-[#141416]">
                  «ИНВЕСТИЦИОННЫЙ РЫЧАГ» (Дорожная карта удвоения капитализации)
                </h3>
              </div>
            </div>
            <span className="font-mono text-[10px] text-white bg-[#8C6D37] px-3.5 py-1.5 rounded-none font-bold uppercase tracking-[0.2em] shadow-sm">
              ДОКУМЕНТ, КОТОРЫЙ ПРОДАЕТ КЛИНИКУ
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-[13px] font-sans">
            {/* Рычаг 1 */}
            <div className="flex flex-col gap-2 p-5 bg-white border border-[#8C6D37]/30 rounded-[2px] shadow-xs">
              <h4 className="text-[15px] font-bold text-[#141416] flex items-center gap-2">
                <TrendingUp size={16} className="text-[#8C6D37]" />
                Roadmap роста мощности:
              </h4>
              <p className="text-[#334155] leading-relaxed font-normal">
                Пошаговая инструкция для покупателя, наглядно показывающая, как за счет дозагрузки свободных 
                кресел (с текущих 40% до нормативных 65%) окупить вложения в покупку клиники в 2 раза быстрее.
              </p>
            </div>

            {/* Рычаг 2 */}
            <div className="flex flex-col gap-2 p-5 bg-white border border-[#8C6D37]/30 rounded-[2px] shadow-xs">
              <h4 className="text-[15px] font-bold text-[#141416] flex items-center gap-2">
                <Zap size={16} className="text-[#8C6D37]" />
                Бесшовный переход контроля (Zero Friction):
              </h4>
              <p className="text-[#334155] leading-relaxed font-normal">
                Регламент передачи операционного управления новому владельцу или наемному директору 
                без сбоев в расписании, потери выручки и увольнения ключевых врачей.
              </p>
            </div>
          </div>
        </div>

        {/* =========================================================================
            4. ИТОГОВЫЙ ИНСТИТУЦИОНАЛЬНЫЙ ВЫВОД
            ========================================================================= */}
        <div className="p-8 sm:p-10 bg-white rounded-[2px] text-center max-w-4xl mx-auto border border-[#EAE5DE] shadow-xs">
          <p className="text-[16px] sm:text-[18px] font-serif font-semibold text-[#141416] leading-relaxed">
            «Клиника без системы управления — это гаражная распродажа подержанных кресел с дисконтом 50%. <br className="hidden sm:inline" />
            <span className="text-[#8C6D37]">Клиника, оцифрованная по стандарту S&P Clinic OS™</span>, — это автономный инвестиционный актив, 
            за который фонды платят максимальный мультипликатор до 7.5x EBITDA».
          </p>
        </div>

        {/* =========================================================================
            5. НАВИГАТОР ПЕРЕХОДА К БЛОКУ 10
            ========================================================================= */}
        <div className="flex justify-center pt-4">
          <Link 
            href="#exit-strategy" 
            className="group inline-flex items-center gap-2 font-mono text-[11px] text-[#64748B] hover:text-[#141416] uppercase tracking-[0.15em] font-semibold transition-all py-3 px-6 hover:bg-black/5 border border-transparent hover:border-[#EAE5DE] rounded-[2px]"
          >
            <span>[ ПЕРЕЙТИ К ЭТАЖУ 10: СТРАТЕГИЯ КАСКАДНОГО ЭКЗИТА ↓ ]</span>
          </Link>
        </div>

      </div>
    </section>
  );
}
